<template>
  <div id="gap-backtest-watchlist">
    <div class="container-fluid pt-4 px-4">
      <v-alert type="info" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        Score is the Wilson lower-bound win rate from the Gap Fill backtest. A watchlist stock is flagged
        <strong>Remove</strong> once it has enough backtest events but its edge no longer clears the eligibility bar
        (min win rate {{ minWinRateThresholdPct !== null ? `${minWinRateThresholdPct}%` : '—' }}, or return lower
        bound ≤ 0) set on the Settings page. Suggested additions are shortlist stocks that already clear the bar but
        aren't in the watchlist yet.
      </v-alert>

      <v-row class="mb-1" dense>
        <v-col cols="12" sm="4">
          <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
            <v-card-text class="px-5 py-4">
              <div class="text-caption text-medium-emphasis">Good</div>
              <div class="stat-value text-success">{{ healthSummary.good }}</div>
              <div class="text-caption text-medium-emphasis mt-1">Cleared the historical edge bar</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
            <v-card-text class="px-5 py-4">
              <div class="text-caption text-medium-emphasis">Weak</div>
              <div class="stat-value" :class="healthSummary.weak > 0 ? 'text-error' : ''">{{ healthSummary.weak }}</div>
              <div class="text-caption text-medium-emphasis mt-1">Enough data, edge no longer clears the bar</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
            <v-card-text class="px-5 py-4">
              <div class="text-caption text-medium-emphasis">Needs Review</div>
              <div class="stat-value">{{ healthSummary.needsReview }}</div>
              <div class="text-caption text-medium-emphasis mt-1">Not enough fade-bias gap history yet</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-3 px-5 py-4">
          <span class="text-h6 font-weight-medium">Watchlist</span>
          <div class="d-flex align-center ga-3">
            <span class="text-caption text-medium-emphasis">
              {{ watchlist.length }} stock{{ watchlist.length === 1 ? '' : 's' }}
            </span>
            <v-btn variant="tonal" color="primary" size="small" :loading="checkingEligibility" @click="checkIntradayEligibility">
              Check Intraday Eligibility
            </v-btn>
            <v-btn variant="text" size="small" :loading="!loaded" @click="load">
              <font-awesome-icon icon="fa-solid fa-rotate-right" class="mr-1" /> Refresh
            </v-btn>
          </div>
        </v-card-title>
        <div v-if="eligibilityError" class="px-5 pb-3 text-caption text-error">{{ eligibilityError }}</div>

        <v-data-table
          :headers="headers"
          :items="watchlist"
          :loading="!loaded"
          item-value="code"
          fixed-header
          :items-per-page="-1"
          hide-default-footer
          class="custom-data-table"
        >
          <template v-slot:[`item.source`]="{ item }">
            <v-chip size="small" variant="tonal" :color="item.source === 'universe' ? 'info' : 'default'">
              {{ item.source === 'universe' ? 'Universe' : 'Portfolio' }}
            </v-chip>
          </template>

          <template v-slot:[`item.intradayAllowed`]="{ item }">
            <font-awesome-icon
              v-if="item.intradayAllowed === true" icon="fa-solid fa-check" class="text-success"
              title="Currently allowed for intraday (NSE)"
            />
            <font-awesome-icon
              v-else-if="item.intradayAllowed === false" icon="fa-solid fa-xmark" class="text-error"
              title="NOT currently allowed for intraday -- likely trade-to-trade/surveillance restricted by the exchange"
            />
            <span v-else class="text-medium-emphasis" title="Not checked yet -- click Check Intraday Eligibility">—</span>
          </template>

          <template v-slot:[`item.wilsonLowerBoundPct`]="{ item }">
            <span v-if="item.wilsonLowerBoundPct !== null">{{ item.wilsonLowerBoundPct }}%</span>
            <span v-else class="text-medium-emphasis">—</span>
          </template>

          <template v-slot:[`item.winRatePct`]="{ item }">
            <span v-if="item.winRatePct !== null">{{ item.winRatePct }}%</span>
            <span v-else class="text-medium-emphasis">—</span>
          </template>

          <template v-slot:[`item.fillRatePct`]="{ item }">
            <span v-if="item.fillRatePct !== null">{{ item.fillRatePct }}%</span>
            <span v-else class="text-medium-emphasis">—</span>
          </template>

          <template v-slot:[`item.avgVolume20`]="{ item }">
            <span v-if="item.avgVolume20 !== null">{{ Number(item.avgVolume20).toLocaleString('en-IN') }}</span>
            <span v-else class="text-medium-emphasis">—</span>
          </template>

          <template v-slot:[`item.profitFactor`]="{ item }">
            <span v-if="item.profitFactor !== null">{{ item.profitFactor.toFixed(2) }}</span>
            <span v-else class="text-medium-emphasis">{{ item.gapEventCount ? 'no losses' : '—' }}</span>
          </template>

          <template v-slot:[`item.returnLowerBoundPct`]="{ item }">
            <span v-if="item.returnLowerBoundPct !== null" :class="item.returnLowerBoundPct >= 0 ? 'text-success' : 'text-error'">
              {{ item.returnLowerBoundPct }}%
            </span>
            <span v-else class="text-medium-emphasis">—</span>
          </template>

          <template v-slot:[`item.avgOpenPrice`]="{ item }">
            <span v-if="item.avgOpenPrice !== null">{{ $helper.formatPrice(item.avgOpenPrice) }}</span>
            <span v-else class="text-medium-emphasis">—</span>
          </template>

          <template v-slot:[`item.suggestedStopPct`]="{ item }">
            <span v-if="item.suggestedStopPct !== null">
              <v-tooltip v-if="item.stopSuggestionPercentiles" location="top">
                <template v-slot:activator="{ props }">
                  <span v-bind="props" class="suggested-stop-value">{{ item.suggestedStopPct }}%</span>
                </template>
                <span>
                  P50 {{ item.stopSuggestionPercentiles.p50 }}% · P75 {{ item.stopSuggestionPercentiles.p75 }}% ·
                  P90 {{ item.stopSuggestionPercentiles.p90 }}%
                </span>
              </v-tooltip>
              <span v-else>{{ item.suggestedStopPct }}%</span>
              <span class="text-caption text-medium-emphasis"> (n={{ item.stopSuggestionSampleSize }})</span>
              <div v-if="item.suggestedStopAmountRs !== null" class="text-caption text-error">
                Risk ≈ {{ $helper.formatPrice(item.suggestedStopAmountRs) }} ({{ item.suggestedStopQty }} sh @ {{ $helper.formatPrice(item.referencePrice) }})
              </div>
              <div v-if="item.suggestedProfitAmountRs !== null" class="text-caption text-success">
                Profit ≈ {{ $helper.formatPrice(item.suggestedProfitAmountRs) }} <span class="text-medium-emphasis">(at Return LB %)</span>
              </div>
            </span>
            <span v-else class="text-medium-emphasis">Insufficient data</span>
          </template>

          <template v-slot:[`item.stopBufferPctOverride`]="{ item }">
            <span v-if="item.stopBufferPctOverride !== null">
              {{ item.stopBufferPctOverride }}%
              <div class="text-caption text-medium-emphasis">
                {{ item.stopBufferPctOverrideMethod === 'manual'
                  ? 'Manually set'
                  : `Applied: Method ${item.stopBufferPctOverrideMethod} v${item.stopBufferPctOverrideVersion}` }}
              </div>
              <div v-if="item.overrideStopAmountRs !== null" class="text-caption text-error">
                Risk ≈ {{ $helper.formatPrice(item.overrideStopAmountRs) }} ({{ item.overrideStopQty }} sh)
              </div>
              <div v-if="item.overrideProfitAmountRs !== null" class="text-caption text-success">
                Profit ≈ {{ $helper.formatPrice(item.overrideProfitAmountRs) }}
              </div>
              <div v-if="item.sizingConstraint === 'exposure'" class="text-caption text-warning">
                Capped by exposure, not risk
              </div>
            </span>
            <span v-else class="text-medium-emphasis">
              Default ({{ defaultStopBufferPct }}%)
              <div v-if="item.defaultStopAmountRs !== null" class="text-caption text-error">
                Risk ≈ {{ $helper.formatPrice(item.defaultStopAmountRs) }} ({{ item.defaultStopQty }} sh)
              </div>
              <div v-if="item.defaultProfitAmountRs !== null" class="text-caption text-success">
                Profit ≈ {{ $helper.formatPrice(item.defaultProfitAmountRs) }}
              </div>
              <div v-if="item.sizingConstraint === 'exposure'" class="text-caption text-warning">
                Capped by exposure, not risk
              </div>
            </span>
          </template>

          <template v-slot:[`item.stopOverrideActions`]="{ item }">
            <div class="d-flex align-center ga-1 flex-wrap stop-override-actions">
              <v-btn
                v-if="item.suggestedStopPct !== null"
                size="small" variant="tonal" color="primary"
                :loading="applyingOverride[item.code]"
                @click="applyStopOverride(item)"
              >
                Apply
              </v-btn>
              <v-text-field
                v-model.number="manualOverrideInput[item.code]"
                type="number" step="0.01" density="compact" hide-details variant="outlined"
                class="manual-override-input" placeholder="%"
              />
              <v-btn
                size="small" variant="tonal"
                :loading="savingManualOverride[item.code]"
                @click="saveManualOverride(item.code)"
              >
                Save
              </v-btn>
              <v-btn
                v-if="item.stopBufferPctOverride !== null"
                size="small" variant="text"
                :loading="clearingOverride[item.code]"
                @click="clearStopOverride(item.code)"
              >
                Clear
              </v-btn>
            </div>
            <!-- Live preview of the manually-typed %, before Save -- mirrors
                 sizePosition() client-side (previewFor()) so it updates
                 instantly with no round trip per keystroke. -->
            <div v-if="previewFor(item)" class="text-caption stop-preview">
              <span class="text-error">Risk ≈ {{ $helper.formatPrice(previewFor(item).amountRs) }} ({{ previewFor(item).qty }} sh)</span>
              <span v-if="previewFor(item).profitAmountRs !== null" class="text-success ml-2">
                Profit ≈ {{ $helper.formatPrice(previewFor(item).profitAmountRs) }}
              </span>
            </div>
          </template>

          <template v-slot:[`item.suggestedMaxOpenDeviationPct`]="{ item }">
            <span v-if="item.suggestedMaxOpenDeviationPct !== null">
              <v-tooltip v-if="item.quoteToleranceSuggestionPercentiles" location="top">
                <template v-slot:activator="{ props }">
                  <span v-bind="props" class="suggested-stop-value">{{ item.suggestedMaxOpenDeviationPct }}%</span>
                </template>
                <span>
                  P50 {{ item.quoteToleranceSuggestionPercentiles.p50 }}% · P75 {{ item.quoteToleranceSuggestionPercentiles.p75 }}% ·
                  P90 {{ item.quoteToleranceSuggestionPercentiles.p90 }}%
                </span>
              </v-tooltip>
              <span v-else>{{ item.suggestedMaxOpenDeviationPct }}%</span>
              <span class="text-caption text-medium-emphasis"> (n={{ item.quoteToleranceSuggestionSampleSize }})</span>
            </span>
            <span v-else class="text-medium-emphasis">Insufficient data</span>
          </template>

          <template v-slot:[`item.maxOpenPriceDeviationPctOverride`]="{ item }">
            <span v-if="item.maxOpenPriceDeviationPctOverride !== null">
              {{ item.maxOpenPriceDeviationPctOverride }}%
            </span>
            <span v-else class="text-medium-emphasis">Default ({{ defaultMaxOpenDeviationPct }}%)</span>
          </template>

          <template v-slot:[`item.quoteToleranceOverrideActions`]="{ item }">
            <div class="d-flex align-center ga-1 flex-wrap stop-override-actions">
              <v-btn
                v-if="item.suggestedMaxOpenDeviationPct !== null"
                size="small" variant="tonal" color="primary"
                :loading="applyingQuoteOverride[item.code]"
                @click="applyQuoteOverride(item)"
              >
                Apply
              </v-btn>
              <v-text-field
                v-model.number="manualQuoteOverrideInput[item.code]"
                type="number" step="0.01" density="compact" hide-details variant="outlined"
                class="manual-override-input" placeholder="%"
              />
              <v-btn
                size="small" variant="tonal"
                :loading="savingManualQuoteOverride[item.code]"
                @click="saveManualQuoteOverride(item.code)"
              >
                Save
              </v-btn>
              <v-btn
                v-if="item.maxOpenPriceDeviationPctOverride !== null"
                size="small" variant="text"
                :loading="clearingQuoteOverride[item.code]"
                @click="clearQuoteOverride(item.code)"
              >
                Clear
              </v-btn>
            </div>
          </template>

          <template v-slot:[`item.orderPlaced`]="{ item }">
            <template v-if="item.orderPlaced">
              <TradeSideChip :direction="item.direction" fallback="" class="mr-1" />
              <v-chip :color="statusColor(item.orderStatus)" size="small" variant="tonal">
                {{ statusLabel(item.orderStatus) }}{{ item.quantity ? ` · ${item.quantity} sh` : '' }}
              </v-chip>
            </template>
            <span v-else class="text-medium-emphasis">No order today</span>
          </template>

          <template v-slot:[`item.todaysRewardRisk`]="{ item }">
            <span v-if="todaysRewardRisk(item) !== null" :class="todaysRewardRisk(item) < 1 ? 'text-error' : 'text-success'">
              {{ todaysRewardRisk(item).toFixed(2) }} : 1
            </span>
            <span v-else class="text-medium-emphasis">—</span>
          </template>

          <template v-slot:[`item.recommendation`]="{ item }">
            <v-chip v-if="item.needsRemoval" color="error" size="small" variant="tonal">Remove</v-chip>
            <v-chip v-else-if="!item.inShortlist" color="default" size="small" variant="tonal">Unproven</v-chip>
            <v-chip v-else color="success" size="small" variant="tonal">Keep</v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              size="small"
              variant="tonal"
              :color="item.needsRemoval ? 'error' : 'default'"
              :loading="removing[item.code]"
              @click="removeCode(item.code)"
            >
              Remove
            </v-btn>
          </template>

          <template v-slot:no-data>
            <div class="py-6 text-center text-medium-emphasis">No stocks in the watchlist yet.</div>
          </template>
        </v-data-table>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card">
        <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-3 px-5 py-4">
          <span class="text-h6 font-weight-medium">Suggested Additions</span>
          <div class="d-flex align-center ga-3">
            <span class="text-caption text-medium-emphasis">
              Eligible shortlist stocks not yet on the watchlist
            </span>
            <v-btn-toggle v-model="addCandidatesSourceFilter" density="compact" mandatory divided>
              <v-btn value="all" size="small">All ({{ addCandidates.length }})</v-btn>
              <v-btn value="portfolio" size="small">Portfolio ({{ addCandidatesCountBySource.portfolio }})</v-btn>
              <v-btn value="universe" size="small">Universe ({{ addCandidatesCountBySource.universe }})</v-btn>
            </v-btn-toggle>
          </div>
        </v-card-title>

        <v-data-table
          :headers="addHeaders"
          :items="filteredAddCandidates"
          item-value="code"
          fixed-header
          :items-per-page="-1"
          hide-default-footer
          class="custom-data-table"
        >
          <template v-slot:[`item.source`]="{ item }">
            <v-chip size="small" variant="tonal" :color="item.source === 'universe' ? 'info' : 'default'">
              {{ item.source === 'universe' ? 'Universe' : 'Portfolio' }}
            </v-chip>
          </template>
          <template v-slot:[`item.wilsonLowerBoundPct`]="{ item }">{{ item.wilsonLowerBoundPct }}%</template>
          <template v-slot:[`item.winRatePct`]="{ item }">{{ item.winRatePct }}%</template>
          <template v-slot:[`item.fillRatePct`]="{ item }">{{ item.fillRatePct }}%</template>
          <template v-slot:[`item.avgVolume20`]="{ item }">{{ Number(item.avgVolume20).toLocaleString('en-IN') }}</template>
          <template v-slot:[`item.profitFactor`]="{ item }">
            <span v-if="item.profitFactor !== null">{{ item.profitFactor.toFixed(2) }}</span>
            <span v-else class="text-medium-emphasis">no losses</span>
          </template>
          <template v-slot:[`item.returnLowerBoundPct`]="{ item }">
            <span :class="item.returnLowerBoundPct >= 0 ? 'text-success' : 'text-error'">{{ item.returnLowerBoundPct }}%</span>
          </template>
          <template v-slot:[`item.avgOpenPrice`]="{ item }">{{ $helper.formatPrice(item.avgOpenPrice) }}</template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn size="small" variant="tonal" color="primary" :loading="adding[item.code]" @click="addCode(item.code)">
              Add
            </v-btn>
          </template>

          <template v-slot:no-data>
            <div class="py-6 text-center text-medium-emphasis">No eligible additions right now.</div>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers/fetch-wrapper';
import TradeSideChip from './TradeSideChip.vue';

export default {
  name: 'GapBacktestWatchlist-page',
  components: { TradeSideChip },
  data() {
    return {
      minWinRateThresholdPct: null,
      defaultStopBufferPct: null,
      defaultMaxOpenDeviationPct: null,
      riskPerTradeRs: null,
      maxExposureRs: null,
      minStopBufferPct: null,
      watchlist: [],
      addCandidates: [],
      adding: {},
      removing: {},
      applyingOverride: {},
      savingManualOverride: {},
      clearingOverride: {},
      manualOverrideInput: {},
      applyingQuoteOverride: {},
      savingManualQuoteOverride: {},
      clearingQuoteOverride: {},
      manualQuoteOverrideInput: {},
      healthSummary: { good: 0, weak: 0, needsReview: 0 },
      loaded: true,
      checkingEligibility: false,
      eligibilityError: null,
      headers: [
        { title: 'Code', key: 'code' },
        { title: 'Source', key: 'source' },
        { title: 'Intraday', key: 'intradayAllowed' },
        { title: 'Score (Wilson LB %)', key: 'wilsonLowerBoundPct' },
        { title: 'Win %', key: 'winRatePct' },
        { title: 'Fill Rate %', key: 'fillRatePct' },
        { title: 'Return LB %', key: 'returnLowerBoundPct' },
        { title: 'Profit Factor', key: 'profitFactor' },
        { title: 'Avg Volume', key: 'avgVolume20' },
        { title: 'Price', key: 'avgOpenPrice' },
        { title: 'Sample', key: 'gapEventCount' },
        { title: 'Suggested Stop %', key: 'suggestedStopPct' },
        { title: 'Your Override', key: 'stopBufferPctOverride' },
        { title: '', key: 'stopOverrideActions', sortable: false },
        { title: 'Suggested Quote Tolerance %', key: 'suggestedMaxOpenDeviationPct' },
        { title: 'Your Quote Tolerance', key: 'maxOpenPriceDeviationPctOverride' },
        { title: '', key: 'quoteToleranceOverrideActions', sortable: false },
        { title: 'Order Today', key: 'orderPlaced' },
        { title: "Today's R:R", key: 'todaysRewardRisk' },
        { title: 'Recommendation', key: 'recommendation' },
        { title: '', key: 'actions', sortable: false },
      ],
      addCandidatesSourceFilter: 'all',
      addHeaders: [
        { title: 'Code', key: 'code' },
        { title: 'Source', key: 'source' },
        { title: 'Score (Wilson LB %)', key: 'wilsonLowerBoundPct' },
        { title: 'Win %', key: 'winRatePct' },
        { title: 'Fill Rate %', key: 'fillRatePct' },
        { title: 'Return LB %', key: 'returnLowerBoundPct' },
        { title: 'Profit Factor', key: 'profitFactor' },
        { title: 'Avg Volume', key: 'avgVolume20' },
        { title: 'Price', key: 'avgOpenPrice' },
        { title: 'Sample', key: 'gapEventCount' },
        { title: '', key: 'actions', sortable: false },
      ],
    };
  },
  computed: {
    // Portfolio and universe candidates are each capped independently
    // server-side (see gap-backtest.controllers.js) so a batch of newly
    // onboarded universe stocks can never crowd every one of them out of a
    // single shared top-N slice -- this toggle just narrows the already-safe
    // combined list for display.
    addCandidatesCountBySource() {
      return this.addCandidates.reduce(
        (acc, row) => { acc[row.source] = (acc[row.source] || 0) + 1; return acc; },
        { portfolio: 0, universe: 0 }
      );
    },
    filteredAddCandidates() {
      if (this.addCandidatesSourceFilter === 'all') return this.addCandidates;
      return this.addCandidates.filter((row) => row.source === this.addCandidatesSourceFilter);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loaded = false;
      return fetchWrapper.get('api/gap-backtest/watchlist-review').then((res) => {
        this.minWinRateThresholdPct = res?.data?.minWinRateThresholdPct ?? null;
        this.defaultStopBufferPct = res?.data?.defaultStopBufferPct ?? null;
        this.defaultMaxOpenDeviationPct = res?.data?.defaultMaxOpenDeviationPct ?? null;
        this.riskPerTradeRs = res?.data?.riskPerTradeRs ?? null;
        this.maxExposureRs = res?.data?.maxExposureRs ?? null;
        this.minStopBufferPct = res?.data?.minStopBufferPct ?? null;
        this.watchlist = res?.data?.watchlist || [];
        this.addCandidates = res?.data?.addCandidates || [];
        this.healthSummary = res?.data?.healthSummary || { good: 0, weak: 0, needsReview: 0 };
        this.loaded = true;
      }).catch((err) => {
        console.error('Failed to load watchlist review:', err);
        this.loaded = true;
      });
    },
    // On-demand check against the exact same eligibility source the live
    // order-time gate already consults (helpers/angel-intraday.js) --
    // confirmed 2026-08-26: SANATHAN passed every earlier check and was only
    // caught at the broker after a real rejection. This surfaces the same
    // restriction proactively, for the whole watchlist at once, without
    // waiting for a live order to fail first. Merges into the existing rows
    // by code rather than replacing them, so every other column's data
    // (score, stop %, etc.) survives untouched.
    checkIntradayEligibility() {
      this.checkingEligibility = true;
      this.eligibilityError = null;
      fetchWrapper.post('api/gap-backtest/watchlist/check-intraday-eligibility').then((res) => {
        const byCode = new Map((res?.data || []).map((e) => [e.code, e]));
        this.watchlist = this.watchlist.map((row) => ({
          ...row,
          intradayAllowed: byCode.has(row.code) ? byCode.get(row.code).intradayAllowed : null,
        }));
        this.checkingEligibility = false;
      }).catch((err) => {
        this.eligibilityError = typeof err === 'string' ? err : 'Failed to check intraday eligibility.';
        this.checkingEligibility = false;
      });
    },
    // Live preview of whatever % is currently typed into the manual
    // override input, before Save -- mirrors sizePosition()
    // (app/crons/paper-trading-live.cron.js) client-side so it updates
    // instantly with no round trip per keystroke. Kept deliberately in sync
    // with that formula; if it ever changes, this must change with it.
    // Also mirrors applyStopBufferFloor() -- without this, typing an
    // override tighter than the configured floor would preview a
    // cheaper-looking number than what actually gets used once saved.
    // Returns null (renders nothing) until a usable positive % is typed.
    previewFor(item) {
      const typedPct = this.manualOverrideInput[item.code];
      if (!typedPct || typedPct <= 0 || !item.referencePrice || this.riskPerTradeRs === null || this.maxExposureRs === null) return null;
      const pct = this.minStopBufferPct != null ? Math.max(typedPct, this.minStopBufferPct) : typedPct;

      const riskPerShareRs = item.referencePrice * (pct / 100);
      const maxSharesByRisk = riskPerShareRs > 0 ? Math.floor(this.riskPerTradeRs / riskPerShareRs) : 0;
      const maxSharesByExposure = Math.floor(this.maxExposureRs / item.referencePrice);
      const qty = Math.max(0, Math.min(maxSharesByRisk, maxSharesByExposure));
      if (qty < 1) return null;

      const amountRs = qty * item.referencePrice * (pct / 100);
      const profitAmountRs = item.returnLowerBoundPct > 0
        ? qty * item.referencePrice * (item.returnLowerBoundPct / 100)
        : null;
      return { qty, amountRs, profitAmountRs };
    },
    // Only returns a number once a real order has opened today (entryPrice/
    // targetPrice/initialStopPrice all come from that PaperTrade row) -- a
    // no_signal/skipped_ranked_out code has no real entry yet, so this stays
    // null (renders "—") rather than showing a shadow-sim/estimated ratio.
    todaysRewardRisk(item) {
      return this.$helper.rewardRiskRatio(item.entryPrice, item.targetPrice, item.initialStopPrice);
    },
    statusColor(status) {
      return {
        open: 'info',
        won: 'success',
        lost: 'error',
        closed_flat: 'warning',
        no_signal: 'default',
        skipped_ranked_out: 'default',
      }[status] || 'default';
    },
    statusLabel(status) {
      return {
        open: 'Open',
        won: 'Won',
        lost: 'Lost',
        closed_flat: 'Closed Flat',
        no_signal: 'No Signal',
        skipped_ranked_out: 'Ranked Out',
      }[status] || status;
    },
    addCode(code) {
      this.adding = { ...this.adding, [code]: true };
      fetchWrapper.post('api/paper-trading-settings/watchlist', { code }).then(() => {
        this.load();
      }).catch((err) => {
        console.error(`Failed to add ${code}:`, err);
      }).finally(() => {
        this.adding = { ...this.adding, [code]: false };
      });
    },
    removeCode(code) {
      this.removing = { ...this.removing, [code]: true };
      fetchWrapper.delete(`api/paper-trading-settings/watchlist/${code}`).then(() => {
        this.load();
      }).catch((err) => {
        console.error(`Failed to remove ${code}:`, err);
      }).finally(() => {
        this.removing = { ...this.removing, [code]: false };
      });
    },
    applyStopOverride(item) {
      const code = item.code;
      this.applyingOverride = { ...this.applyingOverride, [code]: true };
      fetchWrapper.put(`api/paper-trading-settings/watchlist/${code}/stop-buffer-override`, {
        stopBufferPctOverride: item.suggestedStopPct,
        method: item.stopSuggestionMethod,
        version: item.stopSuggestionVersion,
      }).then(() => {
        this.load();
      }).catch((err) => {
        console.error(`Failed to apply suggested stop for ${code}:`, err);
      }).finally(() => {
        this.applyingOverride = { ...this.applyingOverride, [code]: false };
      });
    },
    saveManualOverride(code) {
      const value = this.manualOverrideInput[code];
      if (!value || value <= 0) return;
      this.savingManualOverride = { ...this.savingManualOverride, [code]: true };
      fetchWrapper.put(`api/paper-trading-settings/watchlist/${code}/stop-buffer-override`, {
        stopBufferPctOverride: value,
        method: 'manual',
        version: null,
      }).then(() => {
        this.load();
      }).catch((err) => {
        console.error(`Failed to save manual stop override for ${code}:`, err);
      }).finally(() => {
        this.savingManualOverride = { ...this.savingManualOverride, [code]: false };
      });
    },
    clearStopOverride(code) {
      this.clearingOverride = { ...this.clearingOverride, [code]: true };
      fetchWrapper.put(`api/paper-trading-settings/watchlist/${code}/stop-buffer-override`, {
        stopBufferPctOverride: null,
      }).then(() => {
        this.load();
      }).catch((err) => {
        console.error(`Failed to clear stop override for ${code}:`, err);
      }).finally(() => {
        this.clearingOverride = { ...this.clearingOverride, [code]: false };
      });
    },
    applyQuoteOverride(item) {
      const code = item.code;
      this.applyingQuoteOverride = { ...this.applyingQuoteOverride, [code]: true };
      fetchWrapper.put(`api/paper-trading-settings/watchlist/${code}/quote-sanity-override`, {
        maxOpenPriceDeviationPctOverride: item.suggestedMaxOpenDeviationPct,
      }).then(() => {
        this.load();
      }).catch((err) => {
        console.error(`Failed to apply suggested quote tolerance for ${code}:`, err);
      }).finally(() => {
        this.applyingQuoteOverride = { ...this.applyingQuoteOverride, [code]: false };
      });
    },
    saveManualQuoteOverride(code) {
      const value = this.manualQuoteOverrideInput[code];
      if (!value || value <= 0) return;
      this.savingManualQuoteOverride = { ...this.savingManualQuoteOverride, [code]: true };
      fetchWrapper.put(`api/paper-trading-settings/watchlist/${code}/quote-sanity-override`, {
        maxOpenPriceDeviationPctOverride: value,
      }).then(() => {
        this.load();
      }).catch((err) => {
        console.error(`Failed to save manual quote tolerance for ${code}:`, err);
      }).finally(() => {
        this.savingManualQuoteOverride = { ...this.savingManualQuoteOverride, [code]: false };
      });
    },
    clearQuoteOverride(code) {
      this.clearingQuoteOverride = { ...this.clearingQuoteOverride, [code]: true };
      fetchWrapper.put(`api/paper-trading-settings/watchlist/${code}/quote-sanity-override`, {
        maxOpenPriceDeviationPctOverride: null,
      }).then(() => {
        this.load();
      }).catch((err) => {
        console.error(`Failed to clear quote tolerance override for ${code}:`, err);
      }).finally(() => {
        this.clearingQuoteOverride = { ...this.clearingQuoteOverride, [code]: false };
      });
    },
  },
};
</script>

<style scoped>
.suggested-stop-value {
  cursor: help;
  border-bottom: 1px dotted currentColor;
}
.stop-override-actions {
  min-width: 220px;
}
.manual-override-input {
  width: 80px;
}
.stop-preview {
  margin-top: 4px;
}
</style>
